const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.Utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = window.BAO.Utils.getElements(
      `[${this.element._elIdentifier}="item"]`,
      {
        context: html,
      },
    )

    newElements.forEach((el) => newItems.appendChild(el))

    if (direction == null) {
      this.element.els['item-container'].element.innerHTML = ''
      this.element.els['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.element.els['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.element.els['item-container'].element.append(newItems)
    }

    this.renderLoadPrevious(html)
    this.renderLoadMore(html)

    document
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach((image) => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(`[${this.element._elIdentifier}="productpanel"]`)
      .setAttribute('aria-hidden', (newElements.length === 0).toString())
    document
      .querySelector(`[${this.element._elIdentifier}="emptypanel"]`)
      .setAttribute('aria-hidden', (newElements.length > 0).toString())
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.element.els['load-previous'].selector}, ${this.element.els['load-more'].selector}`,
    )

    if (!paginationElement) return null

    return paginationElement === this.element.els['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious && this.element.els['load-previous']) {
      return (this.element.els['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    if (this.element.els['load-previous']) {
      this.element.els['load-previous'].element.innerHTML =
        window.BAO.Utils.getElement(
          this.element.els['load-previous'].selector,
          { context: html },
        ).innerHTML
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    if (this.element.els['load-more']) {
      this.element.els['load-more'].element.innerHTML =
        window.BAO.Utils.getElement(this.element.els['load-more'].selector, {
          context: html,
        }).innerHTML
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils.getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderSelectedFacetsCount(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom, event)
    this.renderActiveFilterCount(newHTMLDom)
    this.renderResultsCount(newHTMLDom)
    this.renderResetButton(newHTMLDom)
    this.renderSubcategories(newHTMLDom)
    this.element.syncSubcategoryCurrent()

    this.element.loading = false
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderSubcategories (document) {
    const currentNav = this.element.els.subcategories?.element
    if (!currentNav) return

    const newNav = window.BAO.Utils.getElement(
      this.element.els.subcategories.selector,
      { context: document },
    )
    if (!newNav) return

    currentNav.innerHTML = newNav.innerHTML
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderFacets (document, event) {
    const getFacetSelector = (facet) =>
      `${this.element.els.facet.selector}[data-index="${facet.dataset.index}"]`

    const currentFacetElements = this.element.els.facet.elements
    const facetThatSubmitted =
      event?.submitter?.closest(this.element.els.facet.selector) ??
      event?.target?.closest(this.element.els.facet.selector)

    const facetsToRenderSimply = event
      ? currentFacetElements.filter((facet) => facet !== facetThatSubmitted)
      : currentFacetElements

    this.element.log('renderFacets', {
      currentFacetElements,
      facetThatSubmitted,
      facetsToRenderSimply,
    })

    facetsToRenderSimply.forEach((facet) => {
      const selector = getFacetSelector(facet)

      const el = window.BAO.Utils.getElement(selector, { context: document })
      if (!el) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.innerHTML = el.innerHTML
    })

    const filterAccordions = window.BAO.Utils.getElement('filter-accordions')
    if (filterAccordions) filterAccordions.updateTabbableElements()
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderSelectedFacetsCount (document, event) {
    const facetsSelected = this.element.els['facets-selected']

    if (!facetsSelected?.exists) return

    const buildSelectorForFacet = (facet) => {
      const selectorParts = {
        class: `.${Array.from(facet.classList).join('.')}`,
        additional: `${facetsSelected.selector}[data-index="${facet.dataset.index}"]`,
      }

      return Object.values(selectorParts).join('')
    }

    const currentFacetCountElements = facetsSelected.elements
    const submittedFacetElement = event?.submitter?.closest(
      facetsSelected.selector,
    )

    const facetsToUpdate = event
      ? currentFacetCountElements.filter((el) => el !== submittedFacetElement)
      : currentFacetCountElements

    facetsToUpdate.forEach((facet) => {
      const selector = buildSelectorForFacet(facet)

      const newFacet = window.BAO.Utils.getElement(selector, {
        context: document,
      })
      if (!newFacet) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.outerHTML = newFacet.outerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacets = this.element.els['active-facets'].elements
    if (activeFacets.length <= 0) return

    activeFacets.forEach((activeFacet) => {
      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: `${this.element.els['active-facets'].selector}`,
      }
      if (activeFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.Utils.getElement(selector)
      if (!currentEl) return

      const hasActiveItems = !!window.BAO.Utils.getElement(
        `[${this.element._elIdentifier}="active-facet"]`,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFilterCount (document) {
    const existingFilterCounts = window.BAO.Utils.getElements(
      this.element.els['filter-count']?.selector,
    )

    const newFilterCount = window.BAO.Utils.getElement(
      this.element.els['filter-count']?.selector,
      { context: document },
    )
    if (!newFilterCount) return

    existingFilterCounts.forEach(
      (element) => (element.innerHTML = newFilterCount.innerHTML),
    )
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderResultsCount (document) {
    const existingResultsCounts = window.BAO.Utils.getElements(
      this.element.externalEls['results-count']?.selector,
    )

    const newResultsCount = window.BAO.Utils.getElement(
      this.element.externalEls['results-count']?.selector,
      { context: document },
    )
    if (!newResultsCount) return

    existingResultsCounts.forEach(
      (element) => (element.innerHTML = newResultsCount.innerHTML),
    )
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderResetButton (document) {
    const existingResetButton = window.BAO.Utils.getElements(
      this.element.els['reset-button']?.selector,
    )

    const newResetButton = window.BAO.Utils.getElement(
      this.element.els['reset-button']?.selector,
      { context: document },
    )
    if (!newResetButton) return

    existingResetButton.forEach(
      (element) => (element.innerHTML = newResetButton.innerHTML),
    )
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get styles () {
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['facets', 'facet', 'product-grid']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()
    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)

    this.debouncedOnSubmit = window.BAO.Utils.debounce(
      (e) => this.onSubmit(e),
      500,
    )
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(this, 'submit', this.onFormSubmit.bind(this))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))

    this.onSubcategoryClick = this.onSubcategoryClick.bind(this)
    this.listeners.add(this, 'click', this.onSubcategoryClick)
    this.syncSubcategoryCurrent()
  }

  /**
   * @param {MouseEvent} event
   *
   * @returns {void}
   */
  onSubcategoryClick (event) {
    if (this.els['subcategory-link']?.exists !== true) return

    const link = event.target.closest(this.els['subcategory-link'].selector)
    if (!link) return
    if (event.defaultPrevented) return
    if (event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }

    const menuUrl = new URL(link.href, window.location.origin)
    const currentUrl = new URL(window.location.href)

    if (
      this.getCollectionHandle(menuUrl.pathname) !==
      this.getCollectionHandle(currentUrl.pathname)
    ) {
      return
    }

    event.preventDefault()

    const nextParams = new URLSearchParams(menuUrl.searchParams)
    const sortBy = currentUrl.searchParams.get('sort_by')
    if (sortBy && nextParams.has('sort_by') === false) {
      nextParams.set('sort_by', sortBy)
    }
    nextParams.delete('page')

    this.renderPage({ searchParams: nextParams.toString() })
  }

  /**
   * @param {string} pathname
   *
   * @returns {string}
   */
  getCollectionHandle (pathname) {
    const match = pathname.match(/\/collections\/([^/]+)/)

    return match ? match[1] : ''
  }

  /**
   * @param {URLSearchParams} params
   *
   * @returns {boolean}
   */
  menuHasFilters (params) {
    for (const key of params.keys()) {
      if (key !== 'page' && key !== 'sort_by' && key !== 'section_id') {
        return true
      }
    }

    return false
  }

  /**
   * @param {URLSearchParams} menuParams
   * @param {URLSearchParams} currentParams
   *
   * @returns {boolean}
   */
  menuParamsMatchCurrent (menuParams, currentParams) {
    for (const [key, value] of menuParams.entries()) {
      if (key === 'page' || key === 'sort_by' || key === 'section_id') continue
      if (currentParams.getAll(key).includes(value) === false) return false
    }

    return true
  }

  /**
   * @returns {void}
   */
  syncSubcategoryCurrent () {
    const links = this.els['subcategory-link']?.elements
    if (!links?.length) return

    const currentUrl = new URL(window.location.href)
    const currentHandle = this.getCollectionHandle(currentUrl.pathname)
    let bestLink = null
    let bestIsFilter = false

    links.forEach((link) => {
      link.removeAttribute('aria-current')

      const menuUrl = new URL(link.href, window.location.origin)
      if (this.getCollectionHandle(menuUrl.pathname) !== currentHandle) return
      if (
        this.menuParamsMatchCurrent(
          menuUrl.searchParams,
          currentUrl.searchParams,
        ) === false
      ) {
        return
      }

      const isFilter = this.menuHasFilters(menuUrl.searchParams)
      if (isFilter === true) {
        bestLink = link
        bestIsFilter = true
      } else if (bestIsFilter === false) {
        bestLink = link
      }
    })

    if (bestLink) bestLink.setAttribute('aria-current', 'page')
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const searchParams = new URLSearchParams(event.target.search)

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onSubmit (event) {
    event.preventDefault()

    const parentForm = event.target.closest('form')
    if (!parentForm) return

    if (!parentForm.classList.contains('clc-ProductGridFacets-form')) {
      return
    }

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  onFormSubmit (event) {
    event.preventDefault()

    const form = event.target
    if (!form.classList.contains('clc-ProductGridFacets-form')) {
      return
    }

    const formData = new FormData(event.target)
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, event })

    if (window.matchMedia('(min-width: 1000px)').matches) return

    this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    const loadMoreLink = event.target.closest(
      this.els?.['load-more-trigger']?.selector,
    )
    const loadPrevLink = event.target.closest(
      this.els?.['load-prev-trigger']?.selector,
    )

    if (!loadMoreLink && !loadPrevLink) return

    const anchor = event.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      await this.renderer.render(html, event)
    }
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${
        searchParams && '?'.concat(searchParams.toString())
      }`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}

class FacetSort extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick)
  }

  handleClick (e) {
    const buttonEl = e.target.closest(this.els.button.selector)
    if (buttonEl && buttonEl.dataset.value && buttonEl.dataset.name) {
      this.updateValues(buttonEl.dataset.value, buttonEl.dataset.name)
    }
  }

  updateValues (value, name) {
    // Update form value
    this.formSort.value = value

    // Update all external select elements
    if (this.externalEls?.value) {
      this.externalEls?.value?.elements.forEach((el) => {
        el.innerHTML = name
      })
    }

    // Dispatch event on form to trigger the update
    this.formSort.dispatchEvent(new Event('input', { bubbles: true }))

    // Update button attributes
    this.els.button.elements.forEach((button) => {
      if (button.dataset.value === value) {
        button.setAttribute('aria-current', 'page')
      } else {
        button.removeAttribute('aria-current')
      }
    })
  }

  get formSort () {
    const selector = '[data-collection-filters-form-el="sort"]'

    return document.querySelector(selector)
  }
}

if (!customElements.get('facet-sort')) {
  customElements.define('facet-sort', FacetSort)
}
